import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { api } from '../services/api'
import toast from 'react-hot-toast'

export interface User {
  id: string
  username: string
  fullName: string
  followers: number
  following: number
  profileImage?: string
  accountAgeYears?: number
  platform: 'soundcloud'
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  needsReAuth: boolean
  login: (platform: 'soundcloud') => Promise<void>
  logout: () => void
  refreshUserData: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined)
    throw new Error('useAuth must be used within an AuthProvider')
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [needsReAuth, setNeedsReAuth] = useState(false)

  const isAuthenticated = !!user

  const login = async (_platform: 'soundcloud') => {
    try {
      setIsLoading(true)
      // Fetch the OAuth URL from the backend so the client ID never appears in frontend code.
      const response = await api.get('/soundcloud/auth-url')
      const authUrl = response.data?.authUrl
      if (!authUrl) {
        toast.error('Could not retrieve login URL. Please try again.')
        setIsLoading(false)
        return
      }
      window.location.href = authUrl
    } catch (error) {
      console.error('Login error:', error)
      toast.error('Failed to initiate login')
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    localStorage.removeItem('user')
    localStorage.removeItem('accessToken')
    toast.success('Logged out successfully')
  }

  const refreshUserData = async () => {
    try {
      const response = await api.get('/soundcloud/profile')
      const profileData = response.data

      // If the backend returns an error object (no token), treat as unauthenticated
      if (profileData?.error) {
        setUser(null)
        localStorage.removeItem('user')
        localStorage.removeItem('accessToken')
        return
      }

      const userData: User = {
        id: profileData.id?.toString() || '',
        username: profileData.username || '',
        fullName: profileData.full_name || '',
        followers: profileData.followers_count || 0,
        following: profileData.followings_count || 0,
        profileImage: profileData.avatar_url,
        accountAgeYears: profileData.accountAgeYears || 0,
        platform: 'soundcloud'
      }

      setUser(userData)
      setNeedsReAuth(false)
      localStorage.setItem('user', JSON.stringify(userData))
    }

    catch (error) {
      console.error('Failed to refresh user data:', error)
      // Clear stale state so the user sees the "connect" prompt rather than a broken UI
      setUser(null)
      setNeedsReAuth(true)
      localStorage.removeItem('user')
      localStorage.removeItem('accessToken')
    }
  }

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const savedUser = localStorage.getItem('user')
        const savedToken = localStorage.getItem('accessToken')

        if (savedUser && savedToken) {
          // Restore user from cache immediately so the UI is not blank
          setUser(JSON.parse(savedUser))
          // Then silently validate the token is still good
          await refreshUserData()
        }

        else
          // No cached session — user needs to connect
          setUser(null)
      }
      
      catch (error) {
        console.error('Auth check failed:', error)
        localStorage.removeItem('user')
        localStorage.removeItem('accessToken')
        setUser(null)
        setNeedsReAuth(true)
      }

      finally {
        setIsLoading(false)
      }
    }

    checkAuthStatus()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshUserData,
    needsReAuth
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
